import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Column, DataTable } from "@/components/ui/data-table";
import { formatPartnerRevenueValue } from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFormat";
import type {
  PartnerAttributionProductRow,
  PartnerAttributionReport,
  PartnerAttributionRoomRow,
} from "@/types/partner-attribution";

interface PartnerAttributionSectionProps {
  report: PartnerAttributionReport | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Header-row controls — e.g. the shared period selector. */
  headerActions?: React.ReactNode;
}

function formatConversion(value: number | null): string {
  if (value == null) {
    return "—";
  }
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Task 0041 (contract confirmed on dev e4fc7ce5, 2026-09-30). Shared across
 * every "Activities by room" surface: tenant hotel-detail revenue page,
 * hotel `/guesteria-partners/:id`, the hotel `/analytics` Guesteria
 * Partners tab, and the Guesteria-admin hotel-card drill-down — each page
 * fetches with its own (hotel- or admin-scoped) hook and passes the
 * resolved report down here.
 */
export const PartnerAttributionSection: React.FC<
  PartnerAttributionSectionProps
> = ({ report, isLoading, isError, headerActions }) => {
  const { t } = useTranslation();

  const roomRows = useMemo<PartnerAttributionRoomRow[]>(() => {
    if (!report) return [];
    // Contract sorts by orders already — sorted defensively so the UI
    // guarantee doesn't depend on the backend never changing that.
    return [...report.byRoom].sort((a, b) => b.orders - a.orders);
  }, [report]);

  const productRows = useMemo<PartnerAttributionProductRow[]>(
    () => report?.byProduct ?? [],
    [report],
  );

  const currency = report?.totals.currency ?? "EUR";

  const roomLabel = (row: PartnerAttributionRoomRow): string => {
    if (row.source === "hotel_wide") {
      return t("partners.detail.attribution.receptionLabel");
    }
    return row.roomNumber || t("partners.detail.attribution.unknownRoomLabel");
  };

  const roomColumns: Column<PartnerAttributionRoomRow>[] = [
    {
      key: "roomNumber",
      header: t("partners.detail.attribution.roomColumn"),
      render: (_value, row) => <span>{roomLabel(row)}</span>,
    },
    {
      key: "clicks",
      header: t("partners.detail.attribution.clicksColumn"),
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: "orders",
      header: t("partners.detail.attribution.ordersColumn"),
      render: (value: number) => <span>{value}</span>,
    },
    {
      // Earned: the hotel's share of confirmed orders (`earnings`, DV-87).
      key: "earnings",
      header: t("partners.detail.attribution.earnedColumn"),
      render: (value: number) => (
        <span>{formatPartnerRevenueValue(value, currency)}</span>
      ),
    },
    {
      // Expected: the hotel's share of orders still estimated. Only the
      // hotel's share is shown, never the order sum (R-098); a response
      // without `expected` renders "—".
      key: "expected",
      header: t("partners.detail.attribution.expectedColumn"),
      render: (value: number | undefined) => (
        <span>
          {typeof value === "number"
            ? formatPartnerRevenueValue(value, currency)
            : "—"}
        </span>
      ),
    },
    {
      key: "conversion",
      header: t("partners.detail.attribution.conversionColumn"),
      render: (value: number | null) => <span>{formatConversion(value)}</span>,
    },
  ];

  const productColumns: Column<PartnerAttributionProductRow>[] = [
    {
      key: "name",
      header: t("partners.detail.attribution.productColumn"),
      className: "min-w-[200px] whitespace-normal",
      render: (_value, row) => (
        <div className="flex flex-col">
          {row.name ? (
            <span>{row.name}</span>
          ) : (
            <span className="text-muted-foreground italic">
              {t("partners.detail.attribution.productNameMissing")}
            </span>
          )}
          {row.productCode ? (
            <span className="text-muted-foreground text-xs">
              {t("partners.detail.attribution.productCode", {
                code: row.productCode,
              })}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "clicks",
      header: t("partners.detail.attribution.clicksColumn"),
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: "orders",
      header: t("partners.detail.attribution.ordersColumn"),
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: "conversion",
      header: t("partners.detail.attribution.conversionColumn"),
      render: (value: number | null) => <span>{formatConversion(value)}</span>,
    },
  ];

  return (
    <Card className="w-full gap-6 py-6">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4 border-b pb-4">
        <CardTitle className="text-base font-semibold">
          {t("partners.detail.attribution.title")}
        </CardTitle>
        {headerActions}
      </CardHeader>
      <CardContent className="flex flex-col gap-6 pt-0">
        {isError ? (
          <p className="text-destructive py-6 text-center text-sm">
            {t("partners.detail.attribution.error")}
          </p>
        ) : (
          <DataTable
            data={roomRows}
            columns={roomColumns}
            loading={isLoading}
            getRowKey={(row) =>
              `${row.source}-${row.roomId ?? row.roomNumber ?? "unknown"}`
            }
            emptyMessage={t("partners.detail.attribution.empty")}
            withSorting={false}
          />
        )}

        {!isError ? (
          <p className="text-muted-foreground text-xs">
            {t("partners.detail.attribution.earnedNote")}
          </p>
        ) : null}

        {!isError && productRows.length > 0 ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-foreground text-sm font-semibold">
              {t("partners.detail.attribution.topProductsTitle")}
            </h3>
            <DataTable
              data={productRows}
              columns={productColumns}
              loading={isLoading}
              getRowKey={(row, index) =>
                row.productCode ?? row.serviceId ?? `product-${index}`
              }
              emptyMessage={t("partners.detail.attribution.empty")}
              withSorting={false}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
